import React, { useState, useEffect, useRef } from 'react';

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isChrome, setIsChrome] = useState(false);
  // Use ref to track if beforeinstallprompt has fired (avoids closure issue)
  const beforeInstallPromptFiredRef = useRef(false);

  useEffect(() => {
    // Check if app is already installed (standalone mode)
    const isStandaloneMode = window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone ||
      document.referrer.includes('android-app://');
    
    setIsStandalone(isStandaloneMode);

    // Don't show if already installed
    if (isStandaloneMode) {
      return;
    }

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Check if mobile device (Android/iOS)
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
      (window.innerWidth <= 768);

    // Check if Chrome/Edge
    const isChromeBrowser = (/Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor)) ||
      /Edg/.test(navigator.userAgent);
    setIsChrome(isChromeBrowser);

    // Check if already shown install prompt (localStorage)
    const installPromptShown = localStorage.getItem('installPromptShown');
    const lastShown = localStorage.getItem('installPromptLastShown');
    const now = Date.now();
    const oneDay = 24 * 60 * 60 * 1000;
    
    // Check if we should show prompt (not shown before or more than 24 hours passed)
    const shouldShow = !installPromptShown || (lastShown && (now - parseInt(lastShown)) > oneDay);
    
    let fallbackTimeout;
    let iosTimeout;
    let installPromptTimeout;
    
    // Listen for beforeinstallprompt event (Android/Desktop)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      beforeInstallPromptFiredRef.current = true; // Mark that event has fired

      if (shouldShow) {
        // Delay showing prompt by 3 seconds after page load
        installPromptTimeout = setTimeout(() => {
          setShowInstallPrompt(true);
        }, 3000);
      }
    };

    // Check if app was just installed
    const handleAppInstalled = () => {
      setDeferredPrompt(null);
      setShowInstallPrompt(false);
      beforeInstallPromptFiredRef.current = false; // Reset ref for future checks
      localStorage.setItem('installPromptShown', 'true');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Fallback: Show prompt even if beforeinstallprompt doesn't fire
    // This helps when PWA criteria are met but event doesn't fire immediately
    // Show for both mobile and desktop - ALWAYS show if icons are missing or event doesn't fire
    if (shouldShow && !iOS) {
      // Check if service worker is registered (indicates PWA readiness)
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.ready.then(() => {
          // Wait a bit more, then show if beforeinstallprompt hasn't fired
          // Mobile: show after 3 seconds, Desktop: show after 5 seconds
          const delay = isMobile ? 3000 : 5000;
          fallbackTimeout = setTimeout(() => {
            setShowInstallPrompt(prev => {
              // Only show if not already shown and not in standalone mode
              const currentStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                window.navigator.standalone ||
                document.referrer.includes('android-app://');
              // Bug 1 Fix: Use ref to check if beforeinstallprompt has fired (avoids closure issue)
              // Only show fallback if the event hasn't fired yet
              if (!beforeInstallPromptFiredRef.current && !currentStandalone) {
                return true;
              }
              return !currentStandalone ? true : prev;
            });
          }, delay);
        }).catch(() => {
          // If service worker fails, still show prompt after delay
          const delay = isMobile ? 3000 : 5000;
          fallbackTimeout = setTimeout(() => {
            const currentStandalone = window.matchMedia('(display-mode: standalone)').matches ||
              window.navigator.standalone ||
              document.referrer.includes('android-app://');
            if (!currentStandalone) {
              setShowInstallPrompt(true);
            }
          }, delay);
        });
      } else {
        // Even without service worker, show after delay for manual install instructions
        const delay = isMobile ? 3000 : 5000;
        fallbackTimeout = setTimeout(() => {
          const currentStandalone = window.matchMedia('(display-mode: standalone)').matches ||
            window.navigator.standalone ||
            document.referrer.includes('android-app://');
          if (!currentStandalone) {
            setShowInstallPrompt(true);
          }
        }, delay);
      }
    }

    // For iOS, always show after delay if not installed
    if (iOS && shouldShow && !isStandaloneMode) {
      iosTimeout = setTimeout(() => {
        const currentStandalone = window.matchMedia('(display-mode: standalone)').matches ||
          window.navigator.standalone ||
          document.referrer.includes('android-app://');
        if (!currentStandalone) {
          setShowInstallPrompt(true);
        }
      }, 5000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      if (fallbackTimeout) clearTimeout(fallbackTimeout);
      if (iosTimeout) clearTimeout(iosTimeout);
      if (installPromptTimeout) clearTimeout(installPromptTimeout);
      // Reset ref on cleanup to ensure fresh state on remount
      beforeInstallPromptFiredRef.current = false;
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      // For iOS, show instructions
      if (isIOS) {
        alert(
          'iOS ನಲ್ಲಿ install ಮಾಡಲು:\n\n' +
          '1. Safari browser ನಲ್ಲಿ Share button (↓) click ಮಾಡಿ\n' +
          '2. "Add to Home Screen" select ಮಾಡಿ\n' +
          '3. "Add" button click ಮಾಡಿ'
        );
        return;
      }
      
      // For Chrome/Desktop, show manual instructions
      if (isChrome) {
        alert(
          'Chrome/Desktop ನಲ್ಲಿ install ಮಾಡಲು:\n\n' +
          '1. Address bar ನಲ್ಲಿ install icon (⊕) click ಮಾಡಿ\n' +
          '2. ಅಥವಾ Menu (⋮) → "Install BizTracker" select ಮಾಡಿ\n' +
          '3. "Install" button click ಮಾಡಿ'
        );
        return;
      }
      
      // Generic instructions
      alert(
        'App install ಮಾಡಲು:\n\n' +
        '1. Browser menu/open options ನೋಡಿ\n' +
        '2. "Install" ಅಥವಾ "Add to Home Screen" option select ಮಾಡಿ\n' +
        '3. Install button click ಮಾಡಿ'
      );
      return;
    }

    try {
      // Show the install prompt
      deferredPrompt.prompt();

      // Wait for the user to respond
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === 'accepted') {
        localStorage.setItem('installPromptShown', 'true');
        localStorage.setItem('installPromptLastShown', Date.now().toString());
      } else {
        localStorage.setItem('installPromptLastShown', Date.now().toString());
      }

      setDeferredPrompt(null);
      setShowInstallPrompt(false);
      beforeInstallPromptFiredRef.current = false; // Reset ref after install attempt
    } catch (error) {
      console.error('Error showing install prompt:', error);
      // Reset ref on error to allow fallback to work
      beforeInstallPromptFiredRef.current = false;
      // Fallback to manual instructions
      if (isChrome) {
        alert(
          'Chrome ನಲ್ಲಿ install ಮಾಡಲು:\n\n' +
          '1. Address bar ನಲ್ಲಿ install icon (⊕) click ಮಾಡಿ\n' +
          '2. ಅಥವಾ Menu (⋮) → "Install BizTracker" select ಮಾಡಿ'
        );
      }
    }
  };

  const handleDismiss = () => {
    setShowInstallPrompt(false);
    beforeInstallPromptFiredRef.current = false; // Reset ref when dismissed to allow fallback on next visit
    localStorage.setItem('installPromptShown', 'true');
    localStorage.setItem('installPromptLastShown', Date.now().toString());
  };

  // Don't show if already installed or if prompt shouldn't be shown
  if (isStandalone || !showInstallPrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-[max(1rem,env(safe-area-inset-bottom))] left-[max(1rem,env(safe-area-inset-left))] right-[max(1rem,env(safe-area-inset-right))] md:left-auto md:right-[max(1rem,env(safe-area-inset-right))] md:w-96 z-50 animate-slide-up">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border-2 border-primary/30 dark:border-primary/40 p-4 flex items-center gap-4">
        <div className="flex-shrink-0">
          <div className="w-12 h-12 bg-gradient-to-br from-primary to-accent rounded-xl flex items-center justify-center shadow-lg">
            <i className="fas fa-download text-white text-xl"></i>
          </div>
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-gray-800 dark:text-slate-100 text-sm mb-1">
            Install BizTracker App
          </h3>
          <p className="text-xs text-gray-600 dark:text-slate-400">
            {isIOS
              ? 'Home screen ಗೆ add ಮಾಡಿ full screen experience ಪಡೆಯಿರಿ'
              : 'Install ಮಾಡಿ offline access ಮತ್ತು faster loading ಪಡೆಯಿರಿ'
            }
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleInstallClick}
            className="px-4 py-2 bg-gradient-to-r from-primary to-primary-dark text-white rounded-lg text-sm font-semibold hover:shadow-lg transition-all"
          >
            Install
          </button>
          <button
            onClick={handleDismiss}
            className="px-3 py-2 text-gray-600 dark:text-slate-400 hover:text-gray-800 dark:hover:text-slate-200 transition-colors"
            aria-label="Dismiss"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
      </div>
    </div>
  );
};

export default InstallPrompt;